/**
 * One definition of the shape an error is reduced to before it is recorded
 * anywhere (IR-1).
 *
 * ## Why this exists rather than reusing what was already there
 *
 * The preview iframe has carried a good version of this since the bridge was
 * written — `_toErrorPayload` / `_sanitizeSource` / `_limitText` in
 * `editing/iframe-bridge-layers/message-handler.js`. It cannot be imported:
 * those are ES5 function declarations inside a STRING that is injected into a
 * sandboxed iframe, and they have no export. The parent then grew its own
 * partial version in `ui/shell.js` (`normalizeIframeErrorPayload`).
 *
 * So there were two, and IR-1 needs a third for the parent window's own
 * `error` / `unhandledrejection` handlers. This is that one definition; the
 * parent imports it, and `error-payload-invariant-tests.js` asserts the
 * iframe's string copy still agrees with `ERROR_CAPS` — the same device
 * `html-escape-tests.js` uses to hold the export-side textual copies equal.
 *
 * ## Why the caps
 *
 * An error payload is recorded into a bounded ring buffer, often repeatedly and
 * often during a failure cascade. Uncapped, one pathological stack evicts every
 * other breadcrumb — the ones that explain how the failure was reached. The
 * numbers are the iframe's, unchanged, because they have survived real use.
 */

export const ERROR_CAPS = Object.freeze({
  name: 120,
  message: 240,
  stack: 1200,
  source: 300
});

export function limitText(value, maxLen) {
  const str = String(value ?? '');
  return str.length <= maxLen ? str : str.slice(0, maxLen);
}

/**
 * A URL reduced to `origin + pathname`.
 *
 * The QUERY AND HASH ARE DROPPED, and that is the point rather than a
 * side-effect: tokens, session ids and email addresses live in query strings,
 * and a bundle that records "which script threw" does not need them to answer
 * that. Also unwraps a doubled origin (`https://a/https://b`), which is how
 * some bundlers and some browsers report a nested script URL.
 */
export function sanitizeErrorSource(value, { origin = '', href = '' } = {}) {
  if (!value) return '';
  let str = String(value);

  const httpAt = str.indexOf('http://', 1);
  const httpsAt = str.indexOf('https://', 1);
  let duplicateAt;
  if (httpAt !== -1 && httpsAt !== -1) duplicateAt = Math.min(httpAt, httpsAt);
  else duplicateAt = Math.max(httpAt, httpsAt);
  if (duplicateAt > 0) str = str.slice(duplicateAt);

  try {
    const base = origin && origin !== 'null' ? origin : href;
    const u = new URL(str, base || undefined);
    return limitText(u.origin + u.pathname, ERROR_CAPS.source);
  } catch {
    // A relative or malformed source is still worth recording, capped.
    return limitText(str, ERROR_CAPS.source);
  }
}

/**
 * Normalize an `ErrorEvent`, a `PromiseRejectionEvent`, or a bare Error into
 * the recorded shape.
 *
 * @param {string} kind 'error' | 'unhandledrejection' | anything a caller names
 * @param {unknown} errLike
 * @param {{origin?: string, href?: string}} [ctx] location, injected so this
 *   stays testable and platform-neutral.
 */
export function normalizeErrorPayload(kind, errLike, ctx = {}) {
  const out = {
    type: kind,
    name: '',
    message: '',
    source: '',
    line: 0,
    col: 0,
    stack: '',
    href: sanitizeErrorSource(ctx.href || '', ctx)
  };
  if (!errLike) {
    out.message = limitText('Unknown error', ERROR_CAPS.message);
    return out;
  }

  // An ErrorEvent, or anything shaped like one.
  const inner = errLike.error ?? null;
  const reason = errLike.reason ?? null;

  if (reason != null) {
    if (typeof reason === 'object') {
      if (reason.name) out.name = limitText(String(reason.name), ERROR_CAPS.name);
      out.message = limitText(reason.message || String(reason), ERROR_CAPS.message);
      if (reason.stack) out.stack = limitText(String(reason.stack), ERROR_CAPS.stack);
    } else {
      out.message = limitText(String(reason), ERROR_CAPS.message);
    }
    return out;
  }

  if (inner && inner.name) out.name = limitText(String(inner.name), ERROR_CAPS.name);
  else if (errLike.name) out.name = limitText(String(errLike.name), ERROR_CAPS.name);

  out.message = limitText(errLike.message || 'Unknown error', ERROR_CAPS.message);
  if (inner && inner.stack) out.stack = limitText(String(inner.stack), ERROR_CAPS.stack);
  else if (errLike.stack) out.stack = limitText(String(errLike.stack), ERROR_CAPS.stack);

  if (errLike.filename) out.source = sanitizeErrorSource(errLike.filename, ctx);
  out.line = Number(errLike.lineno ?? errLike.line ?? 0) || 0;
  out.col = Number(errLike.colno ?? errLike.col ?? 0) || 0;
  return out;
}
